import type { Lap } from '../../domain/lap/Lap'
import { decodeLaps } from '../lapsFormat/lapsCodec'
import { LapsFormatError } from '../lapsFormat/lapsFormatError'
import {
  decodeSessionSummary,
  type SessionSummary,
} from '../summary/sessionSummary'
import { SummaryFormatError } from '../summary/summaryFormatError'
import { PROCESSED_SESSION_VERSION_KEY } from '../versionKey'
import { type ProcessedFilesApi, UnnamedRunError } from './processedFiles'

export interface RunLapsCache {
  getRun(runId: string): Promise<Lap[] | null>
  setRun(runId: string, bytes: Uint8Array): Promise<void>
}

export interface ServerResult {
  laps: Lap[]
  summary: SessionSummary
  fromCache: boolean
  decodeMs?: number
  cacheWriteMs?: number
}

export type ServerResultMiss = 'no result' | 'no laps' | 'laps of another run'

export type ServerResultRead =
  | { result: ServerResult }
  | { missed: ServerResultMiss }
  | { failed: unknown; serverBug: boolean }

export async function readServerResult(
  sessionId: string,
  api: ProcessedFilesApi,
  runLaps?: RunLapsCache
): Promise<ServerResultRead> {
  try {
    const processed = await api.getProcessedSummary(
      sessionId,
      PROCESSED_SESSION_VERSION_KEY
    )
    if (processed === null) return { missed: 'no result' }
    const summary = decodeSessionSummary(processed.text)

    const cached = await runLaps?.getRun(processed.runId)
    if (cached) return { result: { laps: cached, summary, fromCache: true } }

    const fetched = await api.getProcessedLaps(
      sessionId,
      PROCESSED_SESSION_VERSION_KEY
    )
    if (fetched === null) return { missed: 'no laps' }
    if (fetched.runId !== processed.runId) {
      return { missed: 'laps of another run' }
    }
    const decodeStart = Date.now()
    const laps = decodeLaps(fetched.bytes)
    const decodedAt = Date.now()
    await runLaps?.setRun(fetched.runId, fetched.bytes)
    return {
      result: {
        laps,
        summary,
        fromCache: false,
        decodeMs: decodedAt - decodeStart,
        cacheWriteMs: runLaps ? Date.now() - decodedAt : undefined,
      },
    }
  } catch (error) {
    return { failed: error, serverBug: isServerBug(error) }
  }
}

const isServerBug = (error: unknown) =>
  error instanceof SummaryFormatError ||
  error instanceof LapsFormatError ||
  error instanceof UnnamedRunError
